import { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { Add as AddIcon } from '@mui/icons-material';
import { Box } from '@mui/material';

import { Plan } from '../types/plan.types';
import PlanTable from '../components/table/plans-table';
import { usePlans } from '../hooks/use-plans';
import { usePlanSearchStore } from '../stores/plan-search.store';
import {
  PageContainer,
  PageHeader,
  TitleSection,
  ActionButtonContainer,
  CreateButton,
  ORDER_STATUS,
} from '../styles';

import { PageTitle } from '@/components/styles';
import { GlobalSearch } from '@/components/search';
import { Pagination } from '@/components/pagination';
import { getSearchConfig, reloadPropertyOptions } from '@/config/search-configs';
import { isPastDate } from '@/utils/helpers';
import { SELECTED_PLAN_ID_PARAM } from '@/lib/constants';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { FunctionName } from '@/lib/enums';

const isArchiveFilterEnabled = (value: unknown): boolean => {
  return value === true || value === 'true' || value === 1 || value === '1';
};

export const PlansPage = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isLoadingPropertyOptions, setIsLoadingPropertyOptions] = useState(false);

  const {
    searchResults,
    loading,
    currentPage,
    itemsPerPage,
    totalItems,
    selectedPlans,
    sortField,
    sortOrder,
    handleSearch,
    handleSearchChange,
    handleClear,
    handlePageChange,
    handleItemsPerPageChange,
    handleSelectionChange,
    handleSortChange,
    handleArchive,
    handleConfirmed,
    handleUnconfirmed,
    handleActive,
  } = usePlans();

  // Get search state from store
  const searchInputValues = usePlanSearchStore((state) => state.searchInputValues);
  const committedSearchParams = usePlanSearchStore((state) => state.committedSearchParams);
  const previousIncludeArchiveRef = useRef<boolean>(
    isArchiveFilterEnabled(searchInputValues.isArchive)
  );

  // Handle plan click (navigate to detail page)
  const handlePlanClick = useCallback(
    (plan: Plan) => {
      const hasPast = isPastDate(plan.placementDate);
      const hasArchive = plan.isArchive;
      navigate(
        `/plans/${plan.planId}?hasPast=${hasPast}&hasArchive=${hasArchive}&${SELECTED_PLAN_ID_PARAM}=${plan.id}`
      );
    },
    [navigate]
  );

  // Handle create new plan
  const handleCreatePlan = useCallback(() => {
    navigate('/plans/register');
  }, [navigate]);

  // Calculate total pages
  // const totalPages = Math.ceil(totalItems / Math.max(itemsPerPage, 1));

  // Check if any selected plan has Negotiating status
  const hasNegotiatingStatus = useMemo(() => {
    if (!selectedPlans || selectedPlans.length === 0) return false;
    return searchResults.some(
      (plan) => selectedPlans.includes(plan.id) && plan.orderStatus === ORDER_STATUS.NEGOTIATING
    );
  }, [selectedPlans, searchResults]);

  // Common pagination props
  const paginationProps = useMemo(
    () => ({
      currentPage: currentPage - 1,
      totalPages: Math.max(1, Math.ceil(totalItems / itemsPerPage)),
      rowsPerPage: itemsPerPage,
      totalCount: totalItems,
      onPageChange: (page: number) => handlePageChange(page + 1),
      onRowsPerPageChange: handleItemsPerPageChange,
      rowsPerPageOptions: [20, 50, 100],
      showDeleteButton: false,
      showFilterButtons: true,
      apiResponseInfo: {
        currentPage: currentPage - 1,
        totalPages: Math.max(1, Math.ceil(totalItems / itemsPerPage)),
        isLoading: loading,
      },
    }),
    [
      currentPage,
      totalItems,
      itemsPerPage,
      selectedPlans,
      handlePageChange,
      handleItemsPerPageChange,
      handleConfirmed,
      handleUnconfirmed,
      handleArchive,
      handleActive,
      hasNegotiatingStatus,
      loading,
    ]
  );

  // Get search config for plans
  const handleSearchChangeWithArchiveDetection = useCallback(
    (params: Record<string, unknown>) => {
      const currentIncludeArchive = isArchiveFilterEnabled(params.isArchive);
      const includeArchiveChanged = currentIncludeArchive !== previousIncludeArchiveRef.current;

      if (includeArchiveChanged) {
        previousIncludeArchiveRef.current = currentIncludeArchive;
        setIsLoadingPropertyOptions(true);

        if (typeof reloadPropertyOptions !== 'function') {
          setIsLoadingPropertyOptions(false);
          handleSearchChange(params);
          return;
        }

        // Reload options only; keep user's current input value
        reloadPropertyOptions(FunctionName.SCHEDULE_MANAGEMENT, currentIncludeArchive)
          .then(() => {})
          .catch((error) => {
            console.error('Error reloading property options for plans:', error);
          })
          .finally(() => {
            setIsLoadingPropertyOptions(false);
          });
      }

      handleSearchChange(params);
    },
    [handleSearchChange]
  );

  const handleClearWithReset = useCallback(() => {
    previousIncludeArchiveRef.current = false;
    handleClear();

    if (typeof reloadPropertyOptions !== 'function') {
      return;
    }

    setIsLoadingPropertyOptions(true);
    reloadPropertyOptions(FunctionName.SCHEDULE_MANAGEMENT, false)
      .then(() => {})
      .catch((error) => {
        console.error('Error resetting property options for plans:', error);
      })
      .finally(() => {
        setIsLoadingPropertyOptions(false);
      });
  }, [handleClear]);

  const searchConfig = useMemo(() => {
    const config = getSearchConfig('plans');
    return {
      ...config,
      initialValues: searchInputValues, // Pass initial values from store to restore form fields
      onSearch: (params: Record<string, unknown>) => {
        handleSearch(params);
      },
      onChange: (params: Record<string, unknown>) => {
        handleSearchChangeWithArchiveDetection(params);
      },
      onClear: handleClearWithReset,
      loading: loading || isLoadingPropertyOptions,
    };
  }, [
    handleSearch,
    handleSearchChangeWithArchiveDetection,
    handleClearWithReset,
    loading,
    isLoadingPropertyOptions,
    searchInputValues,
  ]);

  // Set default itemsPerPage based on device type
  useEffect(() => {
    const defaultItemsPerPage = isMobile ? 20 : 100;
    if (itemsPerPage !== defaultItemsPerPage) {
      handleItemsPerPageChange(defaultItemsPerPage);
    }
  }, [isMobile]); // Run when isMobile changes

  // Restore search when navigating back - trigger search if there are committed params
  useEffect(() => {
    // Trigger search if there are committed params (form fields are restored via initialValues)
    // Preserve page number when restoring
    if (Object.keys(committedSearchParams).length > 0) {
      handleSearch(committedSearchParams, true); // Preserve page number
    }
  }, []); // Only run on mount

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader>
        <TitleSection>
          <PageTitle variant='h6'>予定一覧</PageTitle>
        </TitleSection>
        <ActionButtonContainer>
          <CreateButton variant='contained' startIcon={<AddIcon />} onClick={handleCreatePlan}>
            予定登録
          </CreateButton>
        </ActionButtonContainer>
      </PageHeader>

      {/* Search */}
      <Box sx={{ marginBottom: '24px' }}>
        <GlobalSearch config={searchConfig} />
      </Box>

      {/* Top Pagination */}
      <Box sx={{ mb: 1.5 }}>
        {!isMobile ? (
          <Pagination
            {...paginationProps}
            selectedCount={selectedPlans?.length || 0}
            onConfirmed={() => handleConfirmed(selectedPlans || [])}
            onUnconfirmed={() => handleUnconfirmed(selectedPlans || [])}
            onArchived={() => handleArchive(selectedPlans || [])}
            onActive={() => handleActive(selectedPlans || [])}
            hasNegotiatingStatus={hasNegotiatingStatus}
          />
        ) : (
          <Pagination {...paginationProps} showPageNavigation={false} />
        )}
      </Box>
      {/* Table */}
      <Box sx={{ marginBottom: '24px' }}>
        <PlanTable
          data={searchResults}
          loading={loading}
          onPlanClick={handlePlanClick}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          selectedPlans={selectedPlans}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onSelectionChange={handleSelectionChange}
          sortField={sortField}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
          hidePagination={true}
          isMobile={isMobile}
        />
      </Box>
      {/* Bottom Pagination (Mobile only) */}
      {isMobile && (
        <Box sx={{ mb: 1.5 }}>
          <Pagination {...paginationProps} showPerPageSelector={false} showRecordsInfo={false} />
        </Box>
      )}
    </PageContainer>
  );
};
